import moment from "moment";
import process from "process";
import { SlackNotificationType } from "../shared/enums";
import { sendSlack } from "./slack-sender";

export {};

/**
 * Formats a Slack mrkdwn message with a timestamped title, optional italic summary,
 * and an indented list of detail rows. Single source of truth for the standard Slack
 * message layout used by cron routes, server actions, and venue notifications.
 *
 * Note: callers are responsible for escaping any untrusted values with `escapeSlackText`
 * before passing them in — this helper does not escape.
 *
 * @param title - The bold headline (prefixed with the current timestamp).
 * @param message - Optional summary line, rendered in italics under the title.
 * @param details - Optional list of `{ title, message }` rows, rendered indented.
 * @returns The formatted Slack mrkdwn string.
 */
export function formatSlackMessage(
  title: string,
  message?: string,
  details?: { title: string; message: string }[]
): string {
  return (
    `*[${moment().format("MMMM Do, h:mm a")}] ${title}*\n${message ? "_" + message + "_\n" : ""}` +
    (details ?? []).map(({ title, message }) => `\t*${title}*: ${message}\n`).reduce((a, b) => a + b, "")
  );
}

/**
 * Posts a message to the `SLACK_WEBHOOK_URL_<type>` webhook through the shared sender. Every call
 * sends immediately: a module-level batch does not survive across serverless invocations, so
 * batched messages were silently lost. NEVER throws; a missing env var or a failed POST is
 * reported by the sender (logs and Sentry).
 *
 * @param type - Which `SLACK_WEBHOOK_URL_<type>` channel to post to.
 * @param title - Bold headline, prefixed with the current timestamp (ignored when `onlyMessage`).
 * @param message - Summary line (italic under the title), or the raw text when `onlyMessage`.
 * @param details - Optional `{ title, message }` rows rendered indented under the title.
 * @param onlyMessage - When true, posts `message` verbatim as the text. A message-less call posts nothing.
 * @param _flush - Unused. Kept so existing call sites compile; there is no batch to flush any more.
 * @returns Resolves once the send was attempted; never rejects.
 */
export async function sendSlackMessageNoAuthForEmails(
  type: SlackNotificationType,
  title: string,
  message?: string,
  details?: { title: string; message: string }[],
  onlyMessage: boolean = false,
  _flush: boolean = false
): Promise<void> {
  if (onlyMessage) {
    if (!message) return;
    await sendSlack({ channel: type, text: message });
    return;
  }

  await sendSlack({ channel: type, text: formatSlackMessage(title, message, details) });
}

// replaces console.log and pastes to slack for debugging purposes

export function debug(message?: any, ...optionalParams: any[]): void {
  console.log(message, optionalParams);

  let valueFormatted = "";
  for (const value of [message, ...optionalParams]) {
    try {
      const converted = JSON.stringify(value);

      valueFormatted += valueFormatted.length === 0 ? "" : " ";
      valueFormatted += converted;
    } catch (e) {}
  }

  sendSlackMessageNoAuthForEmails(SlackNotificationType.EMAIL, "", valueFormatted, undefined);
} /**
 * Retrieve or refresh credentials for Gmail API.
 * @returns {Promise<OAuth2Client>} The authorized OAuth2 client
 */

// Helper Functions

export async function sendSlackNotification(title: string, details: Record<string, any>, webhookUrl: string) {
  try {
    const message = `*${title}*\n${Object.entries(details)
      .map(([key, value]) => `*${key}:* ${value}`)
      .join("\n")}`;

    await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: message }),
    });
  } catch (error) {
    console.error("Failed to send Slack notification:", error);
  }
}
